<script setup lang="ts">
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import { useChampionshipRegistrationSelectTeamData } from "@/pages/Tasks/Tasks/championship-registration"
import { useRegistrationPolicy } from "@/pages/Tasks/Tasks/useRegistrationPolicy"
import { defineTranslations } from "@/utils/defineTranslations"
import { injectMandatory, useHttpClient } from "@/vf"
import { computed } from "vue"
import { TaskViewMode } from "../../Issue/util"

const props = defineProps<{
    registrationId: string
    team: ChampionshipRegistrationTeam
}>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const http = useHttpClient()
const viewMode = injectMandatory(TaskViewMode, "Missing mandatory injection: TaskViewMode")
const data = useChampionshipRegistrationSelectTeamData()
const policy = useRegistrationPolicy(
    computed(() => data.value?.permissions),
    computed(() => viewMode),
)

const cancelConfirm = createInlineConfirm({
    async action() {
        if (props.team.approvalState === "canceled") {
            return
        }

        await http.post(
            `/championship/registration/${props.registrationId}/team/${props.team.id}/cancel-request?viewMode=${viewMode}`,
        )
    },
    afterAction() {
        emit("updated")
    },
})

const trans = defineTranslations({
    cancel_team_confirmation: {
        de: "Möchtest du dieses Team wirklich stornieren?",
        en: "Do you really want to cancel this team?",
    },
    cancel_team_success: {
        de: "Dein Team wurde storniert.",
        en: "Your team was canceled.",
    },
    cancel_team_button: {
        de: "Team endgültig stornieren",
        en: "Cancel team permanently",
    },
    cancel_team_hint_before_deadline: {
        de: "Die Stornierung ist sofort wirksam und kostenfrei, da der Meldeschluss noch nicht erreicht ist.",
        en: "The cancellation takes effect immediately and is free of charge, as the registration deadline has not been reached yet.",
    },
})
</script>

<template>
    <div>
        <div class="my-3">
            <template v-if="data?.isRegistrationClosed">
                {{ $t("@tasks:tasks.championship_registration.edit_team_wizard.cancel_team_hint") }}
            </template>
            <template v-else>
                {{ trans("cancel_team_hint_before_deadline") }}
            </template>
        </div>

        <VarsityInlineConfirmButton
            :controller="cancelConfirm"
            class="btn btn-danger btn-shape-skewed"
            :disabled="!policy.CancelTeamRequest || team.approvalState === 'canceled'"
        >
            {{ trans("cancel_team_button") }}
        </VarsityInlineConfirmButton>

        <VarsityInlineConfirm :controller="cancelConfirm">
            <template #confirmation>
                {{ trans("cancel_team_confirmation") }}
            </template>
            <template #success>
                {{ trans("cancel_team_success") }}
            </template>
        </VarsityInlineConfirm>
    </div>
</template>
